import { useState } from "react";
import { ChevronLeft, ChevronRight, SlidersHorizontal, Table2 } from "lucide-react";

import { useCurrentBudget, useGrid } from "@/lib/budget";
import { useFinance } from "@/lib/hooks";
import { money } from "@/lib/format";
import { useNavigate } from "@/components/app-shell";
import { BudgetGrid } from "@/components/budget-grid";
import { BudgetSwitcher } from "@/components/budget-switcher";
import { FinanceTabs } from "@/components/finance-tabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/** The year overview: a spreadsheet of every budget line, month by month. */
export function FinanceDashboardPage() {
  const { budgets, loading: loadingBudgets } = useFinance();
  const [budgetId, setBudgetId] = useCurrentBudget(budgets);
  const [year, setYear] = useState(() => new Date().getFullYear());

  const { grid, loading, setCell, clearCell } = useGrid(budgetId, year);
  const navigate = useNavigate();

  if (!loadingBudgets && budgets.length === 0) {
    return (
      <div className="space-y-5">
        <FinanceTabs active="finance-dashboard" />
        <EmptyState
          title="No budgets yet"
          body="A budget holds the lines you track month by month. Create one in Configuration to get started."
          onGo={() => navigate("finance-config")}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FinanceTabs active="finance-dashboard" />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg border bg-background p-1">
          <Button variant="ghost" size="icon" aria-label="Previous year" onClick={() => setYear((y) => y - 1)}>
            <ChevronLeft className="size-4" />
          </Button>
          <span className="min-w-16 text-center font-semibold tabular-nums">{year}</span>
          <Button variant="ghost" size="icon" aria-label="Next year" onClick={() => setYear((y) => y + 1)}>
            <ChevronRight className="size-4" />
          </Button>
        </div>

        <BudgetSwitcher budgets={budgets} current={budgetId} onSelect={setBudgetId} />

        {grid && (
          <div className="text-sm text-muted-foreground">
            Balance{" "}
            <span className="font-semibold text-foreground">
              {money(grid.totals.balance.reduce((a, b) => a + b, 0))}
            </span>
          </div>
        )}
      </div>

      {loading && !grid && <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>}

      {grid && (
        <BudgetGrid grid={grid} onSetCell={setCell} onClearCell={clearCell} />
      )}

      {grid && (
        <p className="text-xs text-muted-foreground">
          Type an amount to record it — that also marks the month done. Tab moves across, Enter moves down, Ctrl+Enter
          ticks a month without a figure. Empty a cell to set it back to pending. Amber cells are months gone by with
          nothing recorded. Lines are added and edited on the budget’s own page, from Configuration.
        </p>
      )}
    </div>
  );
}

function EmptyState({ title, body, onGo }: { title: string; body: string; onGo: () => void }) {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
        <Table2 className="size-8 text-muted-foreground" />
        <div>
          <div className="font-medium">{title}</div>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{body}</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={onGo}>
          <SlidersHorizontal className="size-4" /> Open configuration
        </Button>
      </CardContent>
    </Card>
  );
}
